import React from 'react';
import Skeleton, { SkeletonText } from '../../ui/Skeleton';

interface SkeletonHealthCardProps {
  className?: string;
  contentHeight?: string | number;
}

/**
 * Skeleton Health Card Component
 * 
 * Displays a loading placeholder that matches the structure of the HealthCard component
 * Used to provide a consistent loading experience across health tools
 * 
 * @param className - Additional CSS classes
 * @param contentHeight - Height of the content area (default: 'auto')
 */
const SkeletonHealthCard: React.FC<SkeletonHealthCardProps> = ({
  className = '',
  contentHeight = 'auto',
}) => {
  return (
    <div 
      className={`bg-[var(--background-surface)] rounded-lg shadow-sm p-4 ${className}`}
    >
      {/* Card Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <Skeleton 
            width="2rem" 
            height="2rem" 
            borderRadius="0.5rem" 
            className="mr-3" 
          />
          <Skeleton width="8rem" height="1.25rem" />
        </div>
        
        <Skeleton width="4rem" height="1rem" />
      </div>
      
      {/* Card Content */}
      <div 
        style={{ 
          height: typeof contentHeight === 'number' ? `${contentHeight}px` : contentHeight 
        }}
      >
        <div className="space-y-4">
          <SkeletonText lines={3} spacing="0.75rem" />
          
          <div className="my-4">
            <Skeleton height="0.5rem" className="mb-1" />
            <Skeleton height="2rem" className="rounded-md" />
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            <Skeleton height="4rem" className="rounded-md" />
            <Skeleton height="4rem" className="rounded-md" />
          </div>
          
          <SkeletonText lines={2} spacing="0.5rem" lastLineWidth={60} />
        </div>
      </div>
      
      {/* Card Footer */}
      <div className="mt-4">
        <Skeleton width="70%" height="0.75rem" />
      </div>
    </div>
  );
};

/**
 * Skeleton Health Cards Grid
 * 
 * Displays a grid of skeleton health cards
 * 
 * @param count - Number of cards to display (default: 3)
 * @param columns - Number of columns in the grid (default: 3)
 * @param className - Additional CSS classes
 */
export const SkeletonHealthCardsGrid: React.FC<{
  count?: number;
  columns?: number;
  className?: string;
}> = ({
  count = 3,
  columns = 3,
  className = '',
}) => {
  return (
    <div 
      className={`grid grid-cols-1 md:grid-cols-${columns} gap-6 ${className}`}
    >
      {Array.from({ length: count }).map((_, index) => (
        <SkeletonHealthCard key={index} />
      ))}
    </div>
  );
};

export default SkeletonHealthCard; 